import { useCallback, useMemo } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { format, isValid, addDays } from 'date-fns';
import { CandidateFilterStateType } from '@/components/CandidateFilter/CandidateFilter.types';
import { fieldsAndFriendlyLabelsForFilters } from '@/pages/CandidateInfo/candidateInfoFields';
import * as Styles from './CandidateFilterBoxText.styles';

export type CandidateFilterBoxTextProps = {
  onClickCondition: () => void;
  conditionElement?: (props: any) => JSX.Element;
  valueElement?: (props: any) => JSX.Element;
  filter: CandidateFilterStateType;
  isDateRange: boolean;
  isDatePicker: boolean;
  onOptionsClick: (event: any) => void;
};

const elementStyle = {
  textTransform: 'lowercase',
  fontSize: '14px',
  display: 'inherit',
};

const capitalizeStyle = {
  ...elementStyle,
  textTransform: 'capitalize',
  fontWeight: 'bold',
};

const boldElementStyle = {
  ...elementStyle,
  fontWeight: 'bold',
};

const CandidateFilterBoxTextComponent = ({
  conditionElement: ConditionElement,
  valueElement: ValueElement,
  filter,
  isDateRange,
  isDatePicker,
  onOptionsClick,
  onClickCondition,
}: CandidateFilterBoxTextProps): JSX.Element => {
  const { field, condition, value } = filter;
  const dateFormat = 'dd/MM/YYY';
  const isEnglish = field === fieldsAndFriendlyLabelsForFilters.englishLevel;
  const isOwnerId = field === fieldsAndFriendlyLabelsForFilters.ownerId;
  const hasProfilePicture = field === fieldsAndFriendlyLabelsForFilters.pictureUrl;
  const hasFintechExperience = field === fieldsAndFriendlyLabelsForFilters.hasFintechExperience;
  const isDateFormat = [
    fieldsAndFriendlyLabelsForFilters.updatedAt,
    fieldsAndFriendlyLabelsForFilters.createdAt,
  ].includes(field);
  const isValidDate = typeof value === 'string' && isValid(new Date(value));
  const isConditionWithin = condition === 'isWithin';
  const isFieldStatus = field === fieldsAndFriendlyLabelsForFilters.status;
  const areAllFieldsSelected = field && condition && value;
  const isNotDateField = !isDateRange && !isDatePicker;
  const conditionContainsEmpty = condition.includes('Empty');

  const deleteFilterButton = useMemo(() => {
    const formattedField = filter?.field?.toString().toLowerCase().replace(/\s/g, '');
    if (!filter.isOpened) {
      return null;
    }
    return (
      <Styles.DeleteFilterButton
        type="button"
        data-testid={`candidate-filter-options-${formattedField}`}
        onClick={onOptionsClick}
      >
        <MoreHorizIcon sx={{ width: '12px' }} />
      </Styles.DeleteFilterButton>
    );
  }, [filter?.field, filter.isOpened, onOptionsClick]);

  const renderArrowIcon = useMemo(() => {
    return <Styles.CustomExpandMore className="arrow" />;
  }, []);

  const renderFieldText = useMemo(() => {
    if (field === fieldsAndFriendlyLabelsForFilters.firstName) {
      return <Styles.Text>Candidate {field}</Styles.Text>;
    }
    return <Styles.Text>{field}</Styles.Text>;
  }, [field]);

  const renderConditionElement = useMemo(() => {
    const styleToApply = conditionContainsEmpty ? boldElementStyle : elementStyle;
    if (!ConditionElement) {
      return null;
    }

    return (
      <Styles.ConditionButton
        type="button"
        isDisabled={!filter.isOpened}
        className="action-button"
        onClick={(event) => {
          event.stopPropagation();
          onClickCondition();
        }}
      >
        <ConditionElement style={{ ...styleToApply, padding: '0 3px' }} />
        {!filter.value && <Styles.CustomExpandMore className="arrow" />}
      </Styles.ConditionButton>
    );
  }, [ConditionElement, conditionContainsEmpty, filter.isOpened, filter.value, onClickCondition]);

  const RenderValue = useCallback(({ children }) => {
    return <Styles.Value>{children}</Styles.Value>;
  }, []);

  if (
    areAllFieldsSelected &&
    (isEnglish || isOwnerId || hasProfilePicture || hasFintechExperience)
  ) {
    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        {ValueElement && (
          <RenderValue>
            <ValueElement style={capitalizeStyle} />
          </RenderValue>
        )}
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (areAllFieldsSelected && isFieldStatus) {
    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        <RenderValue>
          <Styles.Text isCapitalized isBold>
            {value?.toString()?.toLowerCase()?.replace(/_/g, ' ')}
          </Styles.Text>
        </RenderValue>
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (areAllFieldsSelected && isConditionWithin && Array.isArray(value)) {
    const firstDateRange = format(addDays(new Date(value[0]), 1), dateFormat);
    const endDateRange = format(addDays(new Date(value[1]), 1), dateFormat);
    const formattedDates = `from ${firstDateRange} to ${endDateRange}`;

    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        <RenderValue>
          <Styles.Text isBold>{formattedDates}</Styles.Text>
        </RenderValue>
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (areAllFieldsSelected && isDateFormat && isValidDate) {
    const formattedValue = format(addDays(new Date(value), 1), dateFormat);

    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        <RenderValue>
          <Styles.Text isBold>{formattedValue}</Styles.Text>
        </RenderValue>
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (areAllFieldsSelected && isNotDateField) {
    const formattedValue = Array.isArray(value) ? value.join(', ') : value;

    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        <RenderValue>
          <Styles.Text isBold>{formattedValue}</Styles.Text>
        </RenderValue>
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (field && condition) {
    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderConditionElement}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  if (field) {
    return (
      <Styles.FilterTextArea>
        {renderFieldText}
        {renderArrowIcon}
        {deleteFilterButton}
      </Styles.FilterTextArea>
    );
  }

  return <Styles.Text>Choose a Property</Styles.Text>;
};

export default CandidateFilterBoxTextComponent;
